(() => {
  "use strict";

  const ELEMENT_NAME = "ar-account-page";

  if (customElements.get(ELEMENT_NAME)) return;

  class HzAccountPage extends HTMLElement {
    connectedCallback() {
      if (this.dataset.hzAccountBound === "true") return;

      this.tabs = Array.from(this.querySelectorAll("[data-hz-account-tab]"));
      this.panels = Array.from(this.querySelectorAll("[data-hz-account-panel]"));

      if (!this.tabs.length || !this.panels.length) return;

      this.handleClick = this.handleClick.bind(this);
      this.handleKeydown = this.handleKeydown.bind(this);
      this.handleHashChange = this.handleHashChange.bind(this);
      this.handleOrientationChange = this.handleOrientationChange.bind(this);
      this.orientationMedia = window.matchMedia("(min-width: 1024px)");

      this.addEventListener("click", this.handleClick);
      this.addEventListener("keydown", this.handleKeydown);
      window.addEventListener("hashchange", this.handleHashChange);
      if (this.orientationMedia.addEventListener) {
        this.orientationMedia.addEventListener("change", this.handleOrientationChange);
      } else {
        this.orientationMedia.addListener?.(this.handleOrientationChange);
      }

      this.dataset.hzAccountBound = "true";
      this.dataset.hzAccountReady = "true";
      this.updateTablistOrientation();

      const hashTab = this.getTabFromHash();
      const selectedTab = this.tabs.find((tab) => tab.getAttribute("aria-selected") === "true");
      this.activateTab(hashTab || selectedTab || this.tabs[0], { updateHash: false, focus: false });
    }

    disconnectedCallback() {
      if (this.dataset.hzAccountBound !== "true") return;

      this.removeEventListener("click", this.handleClick);
      this.removeEventListener("keydown", this.handleKeydown);
      window.removeEventListener("hashchange", this.handleHashChange);
      if (this.orientationMedia?.removeEventListener) {
        this.orientationMedia.removeEventListener("change", this.handleOrientationChange);
      } else {
        this.orientationMedia?.removeListener?.(this.handleOrientationChange);
      }

      delete this.dataset.hzAccountBound;
      delete this.dataset.hzAccountReady;
    }

    updateTablistOrientation() {
      const tablist = this.querySelector("[data-hz-account-tablist]");
      if (!tablist) return;
      tablist.setAttribute("aria-orientation", this.orientationMedia?.matches ? "vertical" : "horizontal");
    }

    handleOrientationChange() {
      this.updateTablistOrientation();
    }

    getTabFromHash() {
      if (!window.location.hash) return null;
      const panelId = window.location.hash.slice(1);
      return this.tabs.find((tab) => tab.getAttribute("aria-controls") === panelId) || null;
    }

    getEnabledTabs() {
      return this.tabs.filter((tab) => !tab.hasAttribute("aria-disabled"));
    }

    activateTab(tab, options = {}) {
      if (!tab || !this.tabs.includes(tab)) return;

      const { updateHash = true, focus = false } = options;
      const panelId = tab.getAttribute("aria-controls");
      const targetPanel = this.panels.find((panel) => panel.id === panelId);

      if (!targetPanel) return;

      this.tabs.forEach((candidate) => {
        const isActive = candidate === tab;
        candidate.setAttribute("aria-selected", isActive ? "true" : "false");
        candidate.setAttribute("tabindex", isActive ? "0" : "-1");
        candidate.classList.toggle("hz__accountNavItem--active", isActive);
      });

      this.panels.forEach((panel) => {
        panel.hidden = panel !== targetPanel;
      });

      if (focus) tab.focus({ preventScroll: true });

      if (updateHash && panelId) {
        const nextUrl = `${window.location.pathname}${window.location.search}#${panelId}`;
        try {
          window.history.replaceState(window.history.state, "", nextUrl);
        } catch (error) {
          // Sandboxed previews can block History API writes; tab behavior remains functional.
        }
      }

      const prefersReducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
      tab.scrollIntoView({
        behavior: prefersReducedMotion ? "auto" : "smooth",
        block: "nearest",
        inline: "nearest"
      });
    }

    handleClick(event) {
      const tab = event.target.closest("[data-hz-account-tab]");
      if (!tab || !this.contains(tab)) return;

      event.preventDefault();
      this.activateTab(tab, { updateHash: true, focus: false });
    }

    handleKeydown(event) {
      const currentTab = event.target.closest("[data-hz-account-tab]");
      if (!currentTab || !this.contains(currentTab)) return;

      const tabs = this.getEnabledTabs();
      const currentIndex = tabs.indexOf(currentTab);
      if (currentIndex < 0) return;

      let nextIndex = null;

      switch (event.key) {
        case "ArrowDown":
        case "ArrowRight":
          nextIndex = (currentIndex + 1) % tabs.length;
          break;
        case "ArrowUp":
        case "ArrowLeft":
          nextIndex = (currentIndex - 1 + tabs.length) % tabs.length;
          break;
        case "Home":
          nextIndex = 0;
          break;
        case "End":
          nextIndex = tabs.length - 1;
          break;
        default:
          return;
      }

      event.preventDefault();
      this.activateTab(tabs[nextIndex], { updateHash: true, focus: true });
    }

    handleHashChange() {
      const tab = this.getTabFromHash();
      if (tab) this.activateTab(tab, { updateHash: false, focus: false });
    }
  }

  customElements.define(ELEMENT_NAME, HzAccountPage);
})();
